// src/components/Header.js
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { FiLogOut } from "react-icons/fi";
import api from "../api";
import CloseCashModal from "./CloseCashModal";
import OpenCashModal from "./OpenCashModal";
import MovementModal from "./MovementModal";
import { imprimirTicketApertura, imprimirTicketCierre } from "../utils/imprimirTicketCaja";
import "./Header.css";

export default function Header({ usuario, cerrarSesion, cajaAbierta, setCajaAbierta, onOpenHorarios, horariosAbierto }) {
  const location = useLocation();
  const isPosPage = location.pathname === "/pos" || location.pathname.startsWith("/pos/");
  const [mostrarCierre, setMostrarCierre] = useState(false);
  const [mostrarApertura, setMostrarApertura] = useState(false);
  const [mostrarMovimiento, setMostrarMovimiento] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [cierreData, setCierreData] = useState(null);
  const [menuCajaAbierto, setMenuCajaAbierto] = useState(false);

  if (!usuario) return null;

  const permisosDefinidos = Array.isArray(usuario?.permisos);
  const permisos = permisosDefinidos ? usuario.permisos : [];
  const fallbackSuperadmin = usuario?.rol === "superadmin";
  const tiene = (permiso) => permisos.includes(permiso) || fallbackSuperadmin;

  const inicial = usuario?.nombre?.charAt(0)?.toUpperCase() || "?";
  const puedeIniciar = cajaAbierta === false;
  const puedeCerrar = cajaAbierta === true;
  const puedeMovimiento = cajaAbierta === true;
  const tituloMovimiento = puedeMovimiento
    ? "Registrar ingreso o egreso de efectivo"
    : cajaAbierta == null
      ? "Consultando estado de caja"
      : "Debe iniciar caja antes de registrar movimientos";

  const abrirModalCierre = async () => {
    if (!puedeCerrar) return;
    setCierreData(null);
    setMostrarCierre(true);
    try {
      setCargando(true);
      const { data } = await api.get("/api/caja/pre-cierre", {
        params: { usuario_id: usuario.id },
      });
      setCierreData({
        cierre: null,
        resumen: data.resumen,
        apertura: data.apertura || null,
        movimientos: data.movimientos || [],
      });
    } catch (error) {
      alert(error?.response?.data?.error || "No se pudo cargar el resumen de cierre");
      setMostrarCierre(false);
    } finally {
      setCargando(false);
    }
  };

  const cerrarModalCierre = () => {
    setMostrarCierre(false);
    setCierreData(null);
  };

  const confirmarCierre = async (arqueo = {}) => {
    try {
      setCargando(true);
      const body = { usuario_id: usuario.id };
      if (arqueo?.efectivo_contado != null) {
        body.efectivo_contado = arqueo.efectivo_contado;
      }
      if (arqueo?.desglose_efectivo && typeof arqueo.desglose_efectivo === "object" && Object.keys(arqueo.desglose_efectivo).length > 0) {
        body.desglose_efectivo = arqueo.desglose_efectivo;
      }
      const { data } = await api.post("/api/caja/cerrar", body);
      if (setCajaAbierta) setCajaAbierta(false);
      setCierreData({
        cierre: data.cierre,
        resumen: data.resumen,
        apertura: data.apertura || null,
        movimientos: data.movimientos || [],
      });
      imprimirTicketCierre({
        nombreCajero: usuario.nombre,
        cierre: data.cierre,
        resumen: data.resumen,
        desgloseEfectivo: arqueo?.desglose_efectivo || data.cierre?.desglose_efectivo || null,
        movimientos: data.movimientos || [],
        apertura: data.apertura || null,
      });
    } catch (error) {
      alert(error?.response?.data?.error || "No se pudo cerrar caja");
    } finally {
      setCargando(false);
    }
  };

  const confirmarApertura = async (payload) => {
    try {
      setCargando(true);
      await api.post("/api/caja/aperturar", {
        usuario_id: usuario.id,
        monto_total: payload.monto_total,
        desglose: payload.desglose || undefined,
      });
      if (setCajaAbierta) setCajaAbierta(true);
      setMostrarApertura(false);
      imprimirTicketApertura({
        nombreCajero: usuario.nombre,
        monto_total: payload.monto_total,
        desglose: payload.desglose || null,
        fecha: new Date(),
      });
      alert("Caja iniciada correctamente.");
    } catch (error) {
      alert(error?.response?.data?.error || "No se pudo iniciar caja");
    } finally {
      setCargando(false);
    }
  };

  return (
    <>
      <header className="header with-sidebar">
        <div className="header-left">
          {isPosPage ? (
            <div className="header-brand">
              <img src="/logolasushiteria.png" alt="La Sushitería" className="header-brand__logo" />
              <span className="header-brand__title">La Sushitería</span>
            </div>
          ) : (
            <h1>Caja POS</h1>
          )}
        </div>

        <div className="header-user">
          <button
            className="iniciar-caja-btn"
            onClick={() => puedeIniciar && setMostrarApertura(true)}
            disabled={!puedeIniciar}
            title={cajaAbierta === true ? "Cierre la caja actual para poder iniciar otra" : "Iniciar caja"}
          >
            Iniciar caja
          </button>
          {/* En escritorio: botones visibles */}
          <button
            className="cerrar-caja-btn header-desktop-only"
            onClick={abrirModalCierre}
            disabled={!puedeCerrar}
            title={puedeCerrar ? "Cerrar caja" : "Debe iniciar caja antes de cerrar"}
          >
            Cerrar Caja
          </button>
          {tiene("movimientos") && (
            <button
              className="movimiento-caja-btn header-desktop-only"
              onClick={() => puedeMovimiento && setMostrarMovimiento(true)}
              disabled={!puedeMovimiento}
              title={tituloMovimiento}
            >
              Movimiento
            </button>
          )}
          {/* En móvil: menú de tres puntos con Cerrar caja y Movimiento */}
          {tiene("horarios") && (
            <button
              className={`horarios-btn header-desktop-only${horariosAbierto ? " activo" : ""}`}
              onClick={onOpenHorarios}
              title="Ver horarios disponibles"
            >
              Horarios
            </button>
          )}
          <div className="header-mobile-menu">
            <button
              type="button"
              className="header-mobile-menu-trigger"
              onClick={() => setMenuCajaAbierto((v) => !v)}
              aria-expanded={menuCajaAbierto}
              aria-label="Más opciones de caja"
            >
              <span aria-hidden>⋯</span>
            </button>
            {menuCajaAbierto && (
              <>
                <div className="header-mobile-menu-backdrop" onClick={() => setMenuCajaAbierto(false)} aria-hidden />
                <div className="header-mobile-menu-dropdown">
                  <button
                    type="button"
                    className="cerrar-caja-btn"
                    onClick={() => { setMenuCajaAbierto(false); abrirModalCierre(); }}
                    disabled={!puedeCerrar}
                  >
                    Cerrar caja
                  </button>
                  {tiene("movimientos") && (
                    <button
                      type="button"
                      className="movimiento-caja-btn"
                      onClick={() => { setMenuCajaAbierto(false); if (puedeMovimiento) setMostrarMovimiento(true); }}
                      disabled={!puedeMovimiento}
                      title={tituloMovimiento}
                    >
                      Movimiento
                    </button>
                  )}
                  {tiene("horarios") && (
                    <button
                      type="button"
                      className="horarios-btn"
                      onClick={() => {
                        setMenuCajaAbierto(false);
                        onOpenHorarios?.();
                      }}
                    >
                      Horarios
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="user-info">
            <span className="name">{usuario.nombre}</span>
            <span className="role">{usuario.rol}</span>
          </div>

          <div className="user-avatar">{inicial}</div>

          <button
            className="logout-btn"
            onClick={cerrarSesion}
            title="Cerrar sesión"
          >
            <FiLogOut />
          </button>
        </div>
      </header>

      {mostrarCierre && (
        <CloseCashModal
          cierre={cierreData?.cierre}
          resumen={cierreData?.resumen}
          apertura={cierreData?.apertura}
          movimientos={cierreData?.movimientos}
          cargando={cargando}
          onConfirmar={confirmarCierre}
          onCerrar={cerrarModalCierre}
        />
      )}

      {mostrarApertura && (
        <OpenCashModal
          onCerrar={() => setMostrarApertura(false)}
          onConfirmar={confirmarApertura}
          cargando={cargando}
        />
      )}

      {mostrarMovimiento && (
        <MovementModal
          usuario={usuario}
          onCerrar={() => setMostrarMovimiento(false)}
          onGuardado={() => setMostrarMovimiento(false)}
        />
      )}
    </>
  );
}
